import { useLocation } from "react-router-dom";

import clsx from "clsx";

import Button from "@/design-system/button";
import { BrandPageLink } from "@/src/common/links/BrandPageLink";
import { languageSelector, useUserAuthenticationContext, useViewportInfoContext } from "@/src/contexts";
import {
    DiscountDetailsResources,
    type IDiscountDetailsResources,
} from "@/src/discount/DiscountDetails.Resources";
import { DiscountDetailsResourcesEn } from "@/src/discount/DiscountDetails.Resources.En";
import type { IDiscountReview } from "@/src/discount/DiscountReview";
import DiscountReview from "@/src/discount/DiscountReview";
import type { IDiscountsPanel } from "@/src/discount/DiscountsPanel/DiscountsPanel";
import PageLayout from "@/src/shared/page-layout";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { sendToGTMDiscountDetails, urlFormat } from "@Components/Modules/Helpers";
import { Device } from "@Components/Modules/ViewportResize";
import StickTopPanel from "@Components/StickTopPanel";
import { BrandLink } from "@Routes/ClickLinkLinks";
import { OffersIndex } from "@Routes/OffersLinks";
import * as stylex from "@stylexjs/stylex";

import type { ICashbackDetails } from "../cashback/CashbackDetails";
import CashbackDetails from "../cashback/CashbackDetails";
import type { IGoToShopButton } from "../cashback/GoToShopButton";
import type { IActionButtonBanner } from "../common/ActionButtonBanner";
import type { IContentContainer } from "../common/ContentContainer";
import ContentContainer from "../common/ContentContainer";
import { getUrlParameterByName } from "../common/Helpers";
import type { IImageBanner } from "../common/ImageBanner";
import type { ILoyaltyCardInfo } from "../loyalty-card/LoyaltyCardInfo";
import LoyaltyCardInfo, { LoyaltyCardInfoType } from "../loyalty-card/LoyaltyCardInfo";
import { ComposableSpacing, type QuerySmMax } from "./../../styles/tokens.stylex";
import type { IDiscountBanner } from "./DiscountBanner";
import DiscountBanner from "./DiscountBanner";
import type { IDiscountCoupon } from "./DiscountCoupon";
import { DiscountCoupon } from "./DiscountCoupon";
import type { IDiscountCouponBase } from "./DiscountCouponBase";
import type { IDiscountHeader } from "./DiscountHeader";
import { DiscountHeader } from "./DiscountHeader";
import type { IDiscountNavigation } from "./DiscountNavigation";
import { DiscountNavigation } from "./DiscountNavigation";
import DiscountsPanel from "./DiscountsPanel";

import * as portalCss from "../shared/Portal.module.css";
import * as css from "./DiscountDetails.module.css";

export interface IDiscountDetails extends IComponentAjax<IDiscountDetailsResult> {
    brandId: string;
    allDiscountsLabel: string;
    allDiscountsText: string;
    allDiscountsLink: string;
    coupon: IDiscountCoupon;
    descriptionTextLabel: string;
    descriptionText?: string[];
    validityText: string;
    hasOfferLink?: boolean;
    discountReview: IDiscountReview;
    discountHeader: IDiscountHeader;
    likeAndShare: IActionButtonBanner;
    navigation: IDiscountNavigation;
    sponsoredOffers: IDiscountsPanel;
    featuredOffers: IDiscountsPanel;
    discountImage: IImageBanner;
    invalidDiscountImage: IImageBanner;
    loyaltyCardInfo: ILoyaltyCardInfo;
    invalidDiscount: boolean;
    isAuthenticated: boolean;
    contentContainers?: IContentContainer;
    cashback?: ICashbackDetails;
    device: Device;
    canonicalUrl?: string;
    resources: IDiscountDetailsResources;
}

interface IDiscountDetailsResult {
    isDiscountSaved: boolean;
    isBrandObserved: boolean;
    isCommentDisabled: boolean;
    cashback?: ICashbackDetails;
}

interface IDiscountDetailsState extends IComponentAjaxState, IDiscountDetailsResult {
    discountReview: IDiscountReview;
    disableRotation: boolean;
}

class DiscountDetails extends ComponentAjax<IDiscountDetailsResult, IDiscountDetails, IDiscountDetailsState> {
    constructor(props: IDiscountDetails) {
        super(props);

        this.state = {
            isLoading: false,
            isBrandObserved: false,
            disableRotation: false,
            discountReview: this.props.discountReview,
            isDiscountSaved: this.props.initialData?.isDiscountSaved || false,
            isCommentDisabled: this.props.initialData?.isCommentDisabled || false,
            cashback: props.cashback || this.props.initialData?.cashback,
        };
    }

    private get renderReview() {
        if (this.state.discountReview) {
            return (
                <DiscountReview
                    {...this.state.discountReview}
                    id={this.props.id}
                    brandName={this.props.discountHeader.brandName}
                    isCommentDisabled={
                        this.state.discountReview.isCommentDisabled || this.state.isCommentDisabled
                    }
                    isAuthenticated={this.props.isAuthenticated}
                />
            );
        }
        return null;
    }

    private get renderCoupon() {
        if (!this.props.coupon) {
            return null;
        }

        const couponLoaded = (response: IDiscountCouponBase) => {
            if (!response) {
                return;
            }

            this.setState({ disableRotation: true });
        };

        return <DiscountCoupon {...this.props.coupon} ajaxSuccess={[couponLoaded]} />;
    }

    private get renderWebsiteLink() {
        if (!this.props.hasOfferLink) {
            return null;
        }
        const navigateUrl = urlFormat(BrandLink, { brandId: this.props.brandId, discountId: this.props.id });

        const isStickToTop = this.props.device === Device.Mobile && !this.props.invalidDiscount;

        const websiteLink = (
            <div {...stylex.props(styles.websiteLink)}>
                <Button
                    as="a"
                    variant="lightblue"
                    href={navigateUrl}
                    size="M"
                    target="_blank"
                    styleX={[styles.linkComponent, isStickToTop && styles.stickTopButton]}
                    aria-label={`${this.props.resources.goToOfferButton} ${this.props.discountHeader.brandName}`}
                >
                    {this.props.resources.goToOfferButton}
                </Button>
            </div>
        );

        if (isStickToTop) {
            return <StickTopPanel>{websiteLink}</StickTopPanel>;
        }

        return websiteLink;
    }

    public componentDidMount() {
        const [, , gtmAction, gtmLabel] = window.location.pathname.split("/");

        sendToGTMDiscountDetails("discDetails", gtmAction, gtmLabel);

        if (this.props.initialData) {
            return;
        }

        this.ajaxRequest({ origin: getUrlParameterByName("origin") });
    }

    public render() {
        const className = clsx(css.discountDetailsComponent, this.props.invalidDiscount && css.invalid);

        const discountImage: IDiscountBanner = {
            id: this.props.id,
            likeAndShare: {
                ...this.props.likeAndShare,
                share: {
                    showTooltip: true,
                    text: `${this.props.discountHeader.title}: ${this.props.discountHeader.caption || ""} ${this.props.discountHeader.title}`,
                    shareUrl: this.props.canonicalUrl,
                    title: `Goodie - ${this.props.discountHeader.title}`,
                    appearance: "dark",
                },
            },
            discountImage: this.props.discountImage,
            invalidDiscountImage: this.props.invalidDiscountImage,
        };

        let headerDevices: JSX.Element | null = null;
        let header: JSX.Element | null = null;
        let cashbackDevices: JSX.Element | undefined;
        let cashback: JSX.Element | undefined;

        switch (this.props.device) {
            case Device.Mobile:
                headerDevices = this.renderHeader();
                cashbackDevices = this.renderCashbackDetails();
                break;
            case Device.Tablet:
                headerDevices = this.renderHeader();
                cashback = this.renderCashbackDetails();
                break;
            case Device.Desktop:
            case Device.Laptop:
            default:
                header = this.renderHeader();
                cashback = this.renderCashbackDetails();
        }

        const dHeader = this.props.discountHeader;

        return (
            <PageLayout
                layoutAdUnitPath="okazje"
                slugTarget={this.props.canonicalUrl}
                breadcrumbs={[
                    { labelKey: "offers", path: OffersIndex },
                    {
                        label: dHeader.brandName,
                        path: dHeader.logoTile.href,
                    },
                    {
                        label: dHeader.caption ? `${dHeader.caption} ${dHeader.title}` : dHeader.title,
                    },
                ]}
            >
                <div className={className}>
                    {this.props.navigation && <DiscountNavigation {...this.props.navigation} />}
                    <DiscountBanner {...discountImage} />
                    <div className={portalCss.standardContainer}>
                        {headerDevices}
                        <div className={css.leftColumn}>
                            {header}
                            <div className={css.bodyWrapper}>
                                <div className={css.validity}>
                                    <p
                                        className={css.validityText}
                                        dangerouslySetInnerHTML={{
                                            __html: this.props.validityText,
                                        }}
                                    />
                                </div>
                                {cashbackDevices}
                                {this.renderCoupon}
                                {this.renderWebsiteLink}
                                <h2>{this.props.descriptionTextLabel}</h2>
                                {this.props.descriptionText?.length !== 0 && (
                                    <div className={css.descriptionText}>
                                        {this.props.descriptionText?.map(
                                            DiscountDetails.generateDescriptionTextInnerHtml,
                                        )}
                                    </div>
                                )}
                                {this.props.loyaltyCardInfo && (
                                    <div className={css.aboutLoyalty}>
                                        <LoyaltyCardInfo
                                            {...this.props.loyaltyCardInfo}
                                            loyaltyCardInfoType={LoyaltyCardInfoType.AlignBottom}
                                        />
                                    </div>
                                )}
                                <h2>{this.props.allDiscountsLabel}</h2>
                                {this.props.allDiscountsLink && (
                                    <BrandPageLink
                                        text={this.props.allDiscountsText}
                                        href={this.props.allDiscountsLink}
                                        size="M"
                                        brandId={this.props.brandId}
                                        origin="DiscDetails"
                                    />
                                )}
                                {this.renderAjaxErrorContainer()}
                            </div>
                        </div>
                        <div className={css.rightColumn}>
                            {cashback}
                            {this.renderReview}
                        </div>
                        {this.props.sponsoredOffers && (
                            <DiscountsPanel
                                {...this.props.sponsoredOffers}
                                className={css.sponsoredOffers}
                                titleElement="h4"
                                tileTitleElement="h4"
                                tileDescriptionElement="h3"
                                hideIfEmpty
                            />
                        )}
                        {this.props.featuredOffers && (
                            <DiscountsPanel
                                {...this.props.featuredOffers}
                                className={css.featuredOffers}
                                titleElement="h4"
                                tileTitleElement="h4"
                                tileDescriptionElement="h3"
                                hideIfEmpty
                            />
                        )}
                        <ContentContainer {...this.props.contentContainers} />
                    </div>
                </div>
            </PageLayout>
        );
    }

    protected ajaxSuccess(response: IDiscountDetailsResult) {
        this.setState({
            isLoading: false,
            isDiscountSaved: response.isDiscountSaved,
            isBrandObserved: response.isBrandObserved,
            isCommentDisabled: response.isCommentDisabled,
        });
    }

    protected ajaxFail() {
        return false;
    }

    private renderHeader = () => (
        <DiscountHeader
            {...this.props.discountHeader}
            isBrandObserved={this.state.isBrandObserved}
            canonicalUrl={this.props.canonicalUrl}
        />
    );

    private renderCashbackDetails = () =>
        this.state.cashback && (
            <div className={css.cashbackWrapper}>
                <CashbackDetails
                    {...this.state.cashback}
                    brandName={this.props.discountHeader.brandName}
                    logoUrl={this.props.discountHeader.logoTile.logo.src}
                    brandId={this.props.id}
                    navigateButton={
                        {
                            ...this.state.cashback.navigateButton,

                            telemetryCustom: {
                                eventName: "cshbck-DiscountLocationOnlineClick",
                                actionType: "Click",
                                itemId: this.props.brandId,
                            },
                            discountId: this.props.id,
                            callGTM: () => {
                                sendToGTMDiscountDetails(
                                    "cshbck_discountLocationOnlineClick",
                                    window.location.pathname.split("/")[2],
                                    window.location.pathname.split("/")[3],
                                );
                            },
                        } as IGoToShopButton
                    }
                />
            </div>
        );

    private static generateDescriptionTextInnerHtml = (currentValue: string, index: number) => (
        <div key={index} className={css.descriptionText} dangerouslySetInnerHTML={{ __html: currentValue }} />
    );
}

const DiscountDetailsWrapper = (props: IDiscountDetails) => {
    const { isAuthenticated } = useUserAuthenticationContext();
    const { device } = useViewportInfoContext();
    const { pathname } = useLocation();

    const resources = languageSelector(DiscountDetailsResources, DiscountDetailsResourcesEn);

    return (
        <DiscountDetails
            {...props}
            device={device}
            isAuthenticated={isAuthenticated}
            canonicalUrl={pathname}
            resources={resources}
        />
    );
};

export { DiscountDetailsWrapper as DiscountDetails };

const smMax: QuerySmMax = "@media (max-width: 989px)";
const styles = stylex.create({
    websiteLink: {
        textAlign: "center",
        marginTop: "20px",
        width: "100%",
    },
    linkComponent: {
        borderRadius: ComposableSpacing.s2,
        display: "block",
        marginBottom: 0,
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: 0,
        width: {
            default: "370px",
            [smMax]: "100%",
        },
    },
    stickTopButton: {
        borderRadius: ComposableSpacing.s1,
    },
});
